import React, {
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'
import { LoaderContext } from 'core/context/Loader'
import { AccountContext } from './AccountContext'
import { AccountService, AuthService } from 'api/http'
import { TronLink } from 'core/tron'
import { notification } from 'antd'
import { useFetch } from 'shared/hooks'
import {
    captureReferralRef,
    getReferralRef,
    clearReferralRef,
} from 'shared/utils/referralRef'

export const AccountContextProvider: FC<PropsWithChildren> = React.memo(
    ({ children }) => {
        const { setLoader } = useContext(LoaderContext)
        const [onMeFetch, accountData] = useFetch(AccountService.getMe)

        const [isAuthReady, setIsAuthReady] = useState<boolean>(false)

        /** Авторизация через тронлинк */
        const tronLinkAuth = useCallback(async () => {
            try {
                setLoader(true)

                if (
                    !(await TronLink.ensureConnected()) ||
                    !TronLink.base58Address
                ) {
                    return
                }
                const address = TronLink.base58Address

                const { nonce, isNew } = await AuthService.getNonce({ address })
                if (isNew) {
                    notification.error({
                        message: 'Auth error',
                        description: 'Unregister address tron',
                    })
                    return
                }

                const sign = await TronLink.signMessage(nonce)
                if (!sign) {
                    return
                }
                await AuthService.tronVerify({
                    sign,
                    nonce,
                    address,
                })
                await onMeFetch()
            } catch (e) {
                console.log(e)
            } finally {
                setLoader(false)
            }
        }, [setLoader, onMeFetch])

        /** Регистрация через тронлинк.  Возвращает true при успехе. */
        const tronLinkRegister = useCallback(async (): Promise<boolean> => {
            try {
                setLoader(true)

                if (
                    !(await TronLink.ensureConnected()) ||
                    !TronLink.base58Address
                ) {
                    return false
                }
                const address = TronLink.base58Address

                const { nonce, isNew } = await AuthService.getNonce({ address })
                if (!isNew) {
                    notification.error({
                        message: 'Registration error',
                        description: 'Tron address is exist',
                    })
                    return false
                }

                const sign = await TronLink.signMessage(nonce)
                if (!sign) {
                    return false
                }
                await AuthService.tronVerify({
                    sign,
                    nonce,
                    address,
                    // Авто-привязка реферера из сохранённого ?ref=<id>.
                    referrerId: getReferralRef(),
                })
                // Привязка выполнена (или реферера не было) — метку чистим.
                clearReferralRef()
                await onMeFetch()
                return true
            } catch (e) {
                console.log(e)
                return false
            } finally {
                setLoader(false)
            }
        }, [setLoader, onMeFetch])

        useEffect(() => {
            // Сразу при заходе запоминаем ?ref=<id> из URL (до регистрации).
            captureReferralRef()
            onMeFetch().finally(() => setIsAuthReady(true))
        }, [])

        return (
            <AccountContext.Provider
                value={{
                    accountData,
                    isAuthReady,
                    auth: onMeFetch,
                    tronLinkAuth,
                    tronLinkRegister,
                }}
            >
                {children}
            </AccountContext.Provider>
        )
    }
)
